import React, { useState } from "react";
import { useCompanyStore } from "../../../state/useCompanyStore";
import {
  formatDate,
  formatMoney,
  useGetPaymentCurrencyList,
} from "../../../utils";
import TooltipDropdown from "../../common/tooltipDropdown";
import { ArrowDown, ArrowUp } from "../../../assets/icons";
import SharedPieChart from "../SharedPieChart";
import { useNavigate } from "react-router-dom";

export interface PieData {
  value: number;
  fill: string;
}
interface TrendStatsCardProps {
  title: string;
  titleIcon: any;
  value: string | number;
  percentageValue?: number;
  trendtitle?: string;
  isUptrend: boolean;
  isCashBalance?: boolean;
  toolTipText?: string;
  hide?: boolean;
  showArrow?: boolean;
  selectedLabel: string;
  chartData?: Record<string, PieData> | any;
  expenseValue?: number;
  invoiceValue?: number;
  costOfGoodsSold?: number;
  handleExpandToggle?: () => void;
  handleViewExpense?: () => void;
  businessType?: string;
  prevEndDate?: string;
  prevStartDate?: string;
}

const NetProfitStatCard: React.FC<TrendStatsCardProps> = ({
  title,
  titleIcon,
  isUptrend,
  value,
  trendtitle,
  toolTipText,
  isCashBalance,
  percentageValue,
  hide = false,
  showArrow = false,
  selectedLabel,
  chartData,
  costOfGoodsSold,
  expenseValue,
  invoiceValue,
  handleExpandToggle,
  handleViewExpense,
  businessType,
  prevEndDate,
  prevStartDate,
}) => {
  const navigate = useNavigate();
  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  return (
    <>
      <div className="w-full h-full  flex flex-col justify-between bg-[#ffffff] border border-[#EAECF0] rounded-lg">
        <div className="px-3 pt-3 w-full ">
          <div className="text-[#344054] w-full font-medium flex items-center justify-between">
            {/* title and tooltip */}
            <div className="flex items-center gap-1">
              <p className="text-[14px] text-[#667085] font-medium 1220:text-[13px] xl:text-[14px]  leading-5">
                {title}
              </p>
              {toolTipText && !hide && (
                <div
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {toolTipText && !hide && (
                    <div
                      className="relative"
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    >
                      <TooltipDropdown
                        toolTipText={toolTipText}
                        isHovered={isHovered}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {title == "Total Expenses" &&
              chartData &&
              Object.keys(chartData).length > 3 && (
                <button type="button" onClick={handleExpandToggle}>
                  <svg
                    width={22}
                    height={22}
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.336 10l.53-.53-6.5-6.5-.53.53-.53.53 6.5 6.5.53-.53z"
                      fill="#141B34"
                    />
                    <path
                      d="M8.336 3h-5v5M8.336 21h-5v-5M16.336 3h5v5M16.336 21h5v-5"
                      stroke="#141B34"
                      strokeWidth={1.5}
                    />
                    <path
                      d="M10.336 14l-.53-.53-6.5 6.5.53.53.53.53 6.5-6.5-.53-.53zM14.336 10l.53.53 6.5-6.5-.53-.53-.53-.53-6.5 6.5.53.53zM14.336 14l-.53.53 6.5 6.5.53-.53.53-.53-6.5-6.5-.53.53z"
                      fill="#141B34"
                    />
                  </svg>
                </button>
              )}

            {trendtitle && (
              <p className="text-[14px] text-gray-400 leading-3">
                {trendtitle}
              </p>
            )}
          </div>

          <div className="flex justify-between items-center">
            <div className="relative group cursor-pointer">
              <div className="flex items-center gap-2 mt-5">
                <p className="text-[20px] 1220:text-[16px] xl:text-[20px] text-[#000000] font-semibold leading-6">
                  {Number(value) == 0
                    ? "--"
                    : isCashBalance
                      ? formatMoney(Number(value), userCurrencyCode?.currency)
                      : value}
                </p>
              </div>

              <div className="flex items-center justify-between gap-1 mt-1">
                <p className="text-[20px] 1220:text-[16px] xl:text-[20px] text-[#000000] font-semibold leading-6">
                  {Number(value) !== 0 && showArrow && (
                    <div className="flex items-center gap-1">
                      <div
                        className={` p-1 flex rounded-[4px] gap-1 items-center ${
                          isUptrend ? "" : ""
                        }`}
                      >
                        {isUptrend ? <ArrowUp /> : <ArrowDown />}
                        <h1
                          className={`text-[14px] font-semibold 1220:text-[16px] xl:text-[16px] ${isUptrend ? "text-[#37C89A]" : "text-[#D82020]"}`}
                        >
                          {percentageValue}%
                        </h1>
                      </div>
                    </div>
                  )}
                </p>
              </div>

              {/* Hover Tip */}
              <div className="absolute -bottom-[80px] md:-bottom-[70px] min-w-[319px] md:-right-[300px] -right-[200px] transform -translate-x-1/2 mb-2 hidden group-hover:flex flex-col px-5 py-3 gap-3 bg-white text-white border text-sm rounded-lg shadow-lg whitespace-nowrap z-50">
                <h6 className="text-[#98A2B3] font-medium text-sm tracking-wide text-center">
                  {selectedLabel} vs{" "}
                  {formatDate(new Date(prevStartDate ?? "").toDateString())} -{" "}
                  {formatDate(new Date(prevEndDate ?? "").toDateString())}
                </h6>
                <div className="flex justify-between items-center gap-x-10">
                  <p className="text-[#667085] font-medium text-[14px] ">
                    {title}
                  </p>
                  <p className="text-[#1D2939] flex items-center font-medium text-[14px]">
                    <span>
                      {Number(value) == 0
                        ? "--"
                        : isCashBalance
                          ? formatMoney(
                              Number(value),
                              userCurrencyCode?.currency,
                            )
                          : value}
                    </span>{" "}
                    <span>
                      {Number(value) !== 0 && showArrow && (
                        <div className="flex items-center gap-1">
                          <div
                            className={` p-1 flex rounded-[4px] gap-1 items-center ${
                              isUptrend ? "" : ""
                            }`}
                          >
                            {isUptrend ? <ArrowUp /> : <ArrowDown />}
                            <h1
                              className={`text-[14px] font-medium ${isUptrend ? "text-[#37C89A]" : "text-[#D82020]"}`}
                            >
                              {percentageValue}%
                            </h1>
                          </div>
                        </div>
                      )}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {title == "Total Expenses" && (
              <div>
                <button
                  type="button"
                  onClick={() => {
                    if (Number(value) == 0) {
                      navigate("/expenses/create");
                    } else {
                      handleViewExpense && handleViewExpense();
                    }
                  }}
                  className="border-[#044E97] transition-all duration-500 hover:bg-[#044E97] hover:text-[#ffffff] p-[8px] border rounded-md font-medium text-[#044E97] "
                >
                  {Number(value) == 0 ? "Create Expense" : "View expense"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto px-3 pt-3">
          {title == "Net profit" && (
            <div className="mt-4 flex flex-col gap-2">
              <div className="border bg-[#ECFDF3] border-[#D1FADF] p-[8px] rounded-lg flex items-start justify-between gap-5">
                <p className="text-[#344054] font-medium text-[14px] leading-[22px]">
                  Invoice Value
                </p>
                <p className="text-[#027A48] flex-shrink-0 font-semibold leading-6">
                  {formatMoney(
                    Number(invoiceValue),
                    userCurrencyCode?.currency,
                  )}
                </p>
              </div>
              {businessType == "service" || (
                <div className="border bg-[#E0EEFE] border-[#BBDDFC] p-[8px] rounded-lg flex items-start justify-between gap-5">
                  <p className="text-[#344054] font-medium text-[14px] leading-[22px]">
                    Cost of Goods Sold
                  </p>
                  <p className="text-[#084784] flex-shrink-0 font-semibold leading-6">
                    {formatMoney(
                      Number(costOfGoodsSold),
                      userCurrencyCode?.currency,
                    )}
                  </p>
                </div>
              )}
              <div className="border bg-[#FFFAEB] border-[#FEF0C7] p-[8px] rounded-lg flex items-start justify-between gap-5">
                <p className="text-[#344054] font-medium text-[14px] leading-[22px]">
                  Expenses
                </p>
                <p className="text-[#B54708] flex-shrink-0 font-semibold leading-6">
                  {formatMoney(
                    Number(expenseValue),
                    userCurrencyCode?.currency,
                  )}
                </p>
              </div>
            </div>
          )}

          {title == "Total Expenses" && (
            <div>
              <SharedPieChart data={chartData} baseColor="#F79009" />
            </div>
          )}
        </div>
        <div className="flex w-full bg-[#F9FAFB] justify-center items-center rounded-b-md border-t border-t-[#EAECF0] mt-3">
          <h6 className="w-full text-center text-[#667085] text-[14px] font-medium pt-[15px] pb-[15px]">
            {selectedLabel}
          </h6>
        </div>
      </div>
    </>
  );
};

export default NetProfitStatCard;
